import { useParams, Link } from 'react-router-dom'
import { useMetro } from '../context/MetroContext'

function StationDetails() {
  const { id } = useParams()
  const { getStationById, getLineById, selectedCity, currentCity, isLoading } = useMetro()

  const station = id ? getStationById(id) : undefined
  const stationLines = station?.lines.map(lineId => getLineById(lineId)).filter(Boolean) || []

  if (!selectedCity) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive max-w-4xl">
          <div className="card text-center py-12">
            <span className="text-6xl mb-4 block">🏙️</span>
            <h2 className="text-xl font-semibold mb-2">No City Selected</h2>
            <p className="text-neutral-dark/70 mb-4">Please select a city first</p>
            <Link to="/cities" className="btn-primary">
              Select City
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive max-w-4xl">
          <div className="card text-center py-12">
            <div className="animate-spin w-12 h-12 border-4 border-metro-blue border-t-transparent rounded-full mx-auto mb-4"></div>
            <p>Loading station...</p>
          </div>
        </div>
      </div>
    )
  }

  if (!station) {
    return (
      <div className="py-8 lg:py-12">
        <div className="container-responsive max-w-4xl">
          <div className="card text-center py-12">
            <span className="text-6xl mb-4 block">❓</span>
            <h2 className="text-xl font-semibold mb-2">Station Not Found</h2>
            <p className="text-neutral-dark/70 mb-4">The station "{id}" could not be found</p>
            <Link to="/stations" className="btn-primary">
              Browse All Stations
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="py-8 lg:py-12">
      <div className="container-responsive max-w-4xl">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <Link to="/stations" className="text-metro-blue hover:underline">
            All Stations
          </Link>
          <span className="mx-2 text-neutral-dark/50">/</span>
          <span className="text-neutral-dark/70">{station.name}</span>
        </nav>

        {/* Station Header */}
        <div className="card mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold mb-1">
                {station.name}
              </h1>
              {station.nameHindi && (
                <p className="text-neutral-dark/70">{station.nameHindi}</p>
              )}
              <p className="text-sm text-neutral-dark/50 mt-1">{currentCity?.metroName}</p>
            </div>
            {station.isInterchange && (
              <span className="px-3 py-1 bg-metro-violet-soft text-metro-violet text-sm font-medium rounded-full">
                Interchange
              </span>
            )}
          </div>

          {/* Lines */}
          <div className="flex flex-wrap gap-2">
            {stationLines.map(line => line && (
              <span
                key={line.id}
                className="flex items-center gap-2 px-3 py-1 rounded-full"
                style={{ backgroundColor: `${line.colorHex}20` }}
              >
                <span
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: line.colorHex }}
                ></span>
                <span style={{ color: line.colorHex }}>{line.name}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Link
            to={`/route?from=${station.id}`}
            className="card text-center hover:bg-metro-blue-soft transition-colors"
          >
            <span className="text-2xl mb-2 block">🚇</span>
            <span className="text-sm font-medium">Route From Here</span>
          </Link>
          <Link
            to={`/route?to=${station.id}`}
            className="card text-center hover:bg-metro-blue-soft transition-colors"
          >
            <span className="text-2xl mb-2 block">📍</span>
            <span className="text-sm font-medium">Route To Here</span>
          </Link>
          <Link
            to={`/nearby/tourist?station=${station.id}`}
            className="card text-center hover:bg-metro-blue-soft transition-colors"
          >
            <span className="text-2xl mb-2 block">🏛️</span>
            <span className="text-sm font-medium">Tourist Places</span>
          </Link>
          <Link
            to={`/nearby/hotels?station=${station.id}`}
            className="card text-center hover:bg-metro-blue-soft transition-colors"
          >
            <span className="text-2xl mb-2 block">🏨</span>
            <span className="text-sm font-medium">Nearby Hotels</span>
          </Link>
        </div>

        {/* Station Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Facilities */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">Facilities</h2>
            <ul className="space-y-3">
              {station.hasLift && (
                <li className="flex items-center gap-3">
                  <span className="text-metro-green">✓</span>
                  Lift Available
                </li>
              )}
              {station.hasEscalator && (
                <li className="flex items-center gap-3">
                  <span className="text-metro-green">✓</span>
                  Escalator
                </li>
              )}
              {station.facilities?.map((facility, index) => (
                <li key={index} className="flex items-center gap-3">
                  <span className="text-metro-green">✓</span>
                  {facility}
                </li>
              ))}
              {!station.hasLift && !station.hasEscalator && !station.facilities?.length && (
                <li className="text-neutral-dark/50">Facility information not available</li>
              )}
            </ul>
          </div>

          {/* Gates */}
          {station.gates && station.gates.length > 0 && (
            <div className="card">
              <h2 className="text-xl font-semibold mb-4">Gates / Exits</h2>
              <ul className="space-y-3">
                {station.gates.map((gate, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-full bg-metro-blue-soft text-metro-blue flex items-center justify-center font-semibold">
                      {index + 1}
                    </span>
                    {gate}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Location */}
          {station.coordinates && (
            <div className="card">
              <h2 className="text-xl font-semibold mb-4">Location</h2>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-neutral-dark/70">Latitude</span>
                  <span className="font-mono">{station.coordinates.latitude.toFixed(6)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-dark/70">Longitude</span>
                  <span className="font-mono">{station.coordinates.longitude.toFixed(6)}</span>
                </div>
              </div>
              <a
                href={`https://www.google.com/maps?q=${station.coordinates.latitude},${station.coordinates.longitude}`}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 btn-secondary w-full text-center block"
              >
                Open in Google Maps
              </a>
            </div>
          )}

          {/* Lines Info */}
          <div className="card">
            <h2 className="text-xl font-semibold mb-4">Lines at this Station</h2>
            <div className="space-y-3">
              {stationLines.map(line => line && (
                <div
                  key={line.id}
                  className="p-3 rounded-lg"
                  style={{ backgroundColor: `${line.colorHex}10` }}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: line.colorHex }}
                    ></span>
                    <span className="font-medium">{line.name}</span>
                  </div>
                  <div className="text-sm text-neutral-dark/70">
                    {line.terminal1} ↔ {line.terminal2}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default StationDetails
